import React, { ChangeEvent, Fragment, ReactElement, useEffect, useState } from 'react';
import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Grid2,
    IconButton,
    Menu,
    MenuItem,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import {
    createNewDocumentCompanyDetail,
    createNewDocumentReceiverTemplate,
    saveCompanyDetails,
    updateCompanyName,
    updateCurrency,
} from '../../redux/actions/companyDetailsActions';
import DocumentCompanyDataTemplate from './DocumentCompanyDataTemplate';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DocumentReceiverDataTemplate from './DocumentReceiverDataTemplate';
import DocumentInternalTemplatePreview from './DocumentInternalTemplatePreview';
import DocumentSellingTemplatePreview from './DocumentSellingTemplatePreview';
import SaveIcon from '@mui/icons-material/Save';
import { SaveEntityStatus } from '../../redux/reducers/common';

export default function CompanyDetailsSettings(): ReactElement {
    const companyDetails = useAppSelector((state) => state.companyDetails.companyDetails);
    const documentCompanyDetails = useAppSelector((state) => state.companyDetails.docCompDetails);
    const documentReceiverTemplate = useAppSelector((state) => state.companyDetails.docRecTemplate);

    const saveStatus = useAppSelector((state) => state.companyDetails.saveStatus);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (saveStatus === SaveEntityStatus.Saved) {
            alert('Uspešno sačuvane izmene');
        }
    }, [saveStatus]);

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const addNewDocCompDetail = () => {
        dispatch(createNewDocumentCompanyDetail());
    };

    const addNewDocRecDetail = () => {
        dispatch(createNewDocumentReceiverTemplate());
    };

    const handleInputElementChange = (event: ChangeEvent<HTMLInputElement>) => {
        const target = event.target;
        const value = target.value;
        const targetName = target.name;

        switch (targetName) {
            case 'name':
                dispatch(updateCompanyName(value));
                break;
            case 'currency':
                dispatch(updateCurrency(value));
                break;
            default:
                console.log('invalid field name');
                break;
        }
    };

    const [openInternalDocPreview, setOpenInternalDocPreview] = useState(false);
    const [openSellingDocPreview, setOpenSellingDocPreview] = useState(false);

    const handleOpenInternalDocPreview = () => {
        setOpenInternalDocPreview(true);
        handleClose();
    };

    const handleCloseInternalDocPreview = () => {
        setOpenInternalDocPreview(false);
    };

    const handleOpenSellingDocPreview = () => {
        setOpenSellingDocPreview(true);
        handleClose();
    };

    const handleCloseSellingDocPreview = () => {
        setOpenSellingDocPreview(false);
    };

    const handleSaveCompanyDetails = () => {
        dispatch(saveCompanyDetails());
    };

    return companyDetails === undefined ? (
        <Fragment>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={companyDetails === undefined}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </Fragment>
    ) : (
        <Box sx={{ textAlign: 'start' }}>
            <Box
                sx={{
                    mb: 5,
                    position: 'fixed',
                    backgroundColor: (theme) =>
                        theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
                    width: 'calc(100% - 10px)',
                    p: 5,
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                    }}
                >
                    <Tooltip title="Sačuvaj izmene">
                        <IconButton color="primary" onClick={handleSaveCompanyDetails}>
                            <SaveIcon />
                        </IconButton>
                    </Tooltip>
                    <Typography component="h4" variant="h4">
                        Podešavanja
                    </Typography>
                </Box>
                <hr />
            </Box>
            <Box sx={{ height: 100 }}></Box>
            <Grid2 container spacing={2} sx={{ m: 5 }}>
                <Grid2 size={{ xs: 12 }} mb={4}>
                    <Typography component="h5" variant="h5">
                        Opšte informacije
                    </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6, lg: 4 }}>
                    <TextField
                        error={!!companyDetails?.name && companyDetails?.name !== '' ? false : true}
                        required
                        fullWidth
                        name="name"
                        label="Naziv kompanije"
                        value={!!companyDetails?.name ? companyDetails?.name : ''}
                        variant="outlined"
                        onChange={handleInputElementChange}
                    />
                </Grid2>
                <Grid2 size={{ xs: 0, md: 6, lg: 8 }} />
                <Grid2 size={{ xs: 12, md: 6, lg: 4 }}>
                    <TextField
                        fullWidth
                        name="currency"
                        label="Valuta"
                        value={!!companyDetails?.currency ? companyDetails?.currency : ''}
                        variant="outlined"
                        onChange={handleInputElementChange}
                    />
                </Grid2>
                <Grid2 size={{ xs: 0, md: 6, lg: 8 }} />
                <Grid2 size={{ xs: 12 }}>
                    <hr />
                </Grid2>
                <Grid2 size={{ xs: 12 }} mb={4}>
                    <Typography component="h4" variant="h4">
                        Šabloni{' '}
                        <IconButton color="primary" onClick={handleClick}>
                            <MoreVertIcon />
                        </IconButton>
                    </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12 }} mb={4}>
                    <Typography component="h5" variant="h5">
                        Šablon pošiljaoca otpremnice
                    </Typography>
                </Grid2>
                {documentCompanyDetails?.map((dcd) => {
                    return (
                        <>
                            <Grid2 size={{ xs: 12, lg: 8, xl: 6 }}>
                                <DocumentCompanyDataTemplate docData={dcd} />
                            </Grid2>

                            <Grid2 size={{ xs: 0, lg: 4, xl: 6 }} />
                        </>
                    );
                })}
                <Grid2 size={{ xs: 12, lg: 8, xl: 6 }}>
                    <Button
                        fullWidth
                        color="primary"
                        variant="outlined"
                        onClick={addNewDocCompDetail}
                        sx={{ height: 50 }}
                    >
                        <AddIcon />
                    </Button>
                </Grid2>
                <Grid2 size={{ xs: 0, lg: 4, xl: 6 }} />
                <Grid2 size={{ xs: 12 }}>
                    <hr />
                </Grid2>
                <Grid2 size={{ xs: 12 }} mb={4}>
                    <Typography component="h5" variant="h5">
                        Šablon primaoca otpremnice
                    </Typography>
                </Grid2>
                {documentReceiverTemplate?.map((dcd) => {
                    return (
                        <>
                            <Grid2 size={{ xs: 12, lg: 8, xl: 6 }}>
                                <DocumentReceiverDataTemplate
                                    docData={dcd}
                                    documentReceiverTemplates={documentReceiverTemplate}
                                />
                            </Grid2>

                            <Grid2 size={{ xs: 0, lg: 4, xl: 6 }} />
                        </>
                    );
                })}
                <Grid2 size={{ xs: 12, lg: 8, xl: 6 }}>
                    <Button
                        fullWidth
                        color="primary"
                        variant="outlined"
                        onClick={addNewDocRecDetail}
                        sx={{ height: 50 }}
                    >
                        <AddIcon />
                    </Button>
                </Grid2>
                <Grid2 size={{ xs: 0, lg: 4, xl: 6 }} />
                <Grid2 size={{ xs: 12 }}>
                    <hr />
                </Grid2>
            </Grid2>
            <Menu
                id="demo-customized-menu"
                MenuListProps={{
                    'aria-labelledby': 'demo-customized-button',
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <MenuItem onClick={handleOpenInternalDocPreview} disableRipple>
                    Vidi šablon interne otpremnice
                </MenuItem>
                <MenuItem onClick={handleOpenSellingDocPreview} disableRipple>
                    Vidi šablon prodajne otpremnice
                </MenuItem>
            </Menu>
            <DocumentInternalTemplatePreview
                companyDetails={companyDetails ?? undefined}
                documentCompanyDetails={documentCompanyDetails ?? undefined}
                open={openInternalDocPreview}
                closeForm={handleCloseInternalDocPreview}
            />
            <DocumentSellingTemplatePreview
                companyDetails={companyDetails ?? undefined}
                documentCompanyDetails={documentCompanyDetails ?? undefined}
                documentReceiverTemplate={documentReceiverTemplate ?? undefined}
                open={openSellingDocPreview}
                closeForm={handleCloseSellingDocPreview}
            />
        </Box>
    );
}
